import { getApiError } from '../lib/apiError';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { 
  DollarSign, Users, TrendingUp, Calendar, Percent, 
  FileText, Download, Eye, CreditCard, Loader2, Edit2,
  ChevronRight, BarChart3, PiggyBank, Calculator, Check
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Commissions = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sellers, setSellers] = useState([]);
  const [commissionData, setCommissionData] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [payments, setPayments] = useState([]);
  const [selectedSeller, setSelectedSeller] = useState(null);
  const [sellerSummary, setSellerSummary] = useState(null);
  
  // Filtros
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [groupBy, setGroupBy] = useState('seller');
  
  // Dialogs
  const [editRateDialog, setEditRateDialog] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [detailDialog, setDetailDialog] = useState(false);
  
  // Forms
  const [editRate, setEditRate] = useState({ sellerId: '', rate: 0 });
  const [paymentForm, setPaymentForm] = useState({ sellerId: '', amount: '', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Define período padrão (mês atual)
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    setStartDate(firstDay.toISOString().split('T')[0]);
    setEndDate(today.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      fetchData();
    }
  }, [startDate, endDate, groupBy]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [sellersRes, commissionRes, paymentsRes] = await Promise.all([
        axios.get(`${API_URL}/api/commissions/sellers`),
        axios.get(`${API_URL}/api/commissions/calculate`, {
          params: { start_date: startDate, end_date: endDate }
        }),
        axios.get(`${API_URL}/api/commissions/payments`, {
          params: { start_date: startDate, end_date: endDate }
        })
      ]);
      
      setSellers(sellersRes.data);
      setCommissionData(commissionRes.data);
      setPayments(paymentsRes.data.payments || []);
    } catch (error) {
      toast.error('Erro ao carregar dados de comissões');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/commissions/report`, {
        params: { start_date: startDate, end_date: endDate, group_by: groupBy }
      });
      setReportData(response.data);
    } catch (error) {
      toast.error('Erro ao carregar relatório');
    }
  };

  const fetchSellerSummary = async (sellerId) => {
    try {
      const response = await axios.get(`${API_URL}/api/commissions/summary/${sellerId}`, {
        params: { start_date: startDate, end_date: endDate }
      });
      setSellerSummary(response.data);
      setDetailDialog(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes do vendedor');
    }
  };

  const handleUpdateRate = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/api/commissions/sellers/${editRate.sellerId}/rate?commission_rate=${editRate.rate}`
      );
      toast.success('Taxa de comissão atualizada!');
      setEditRateDialog(false);
      fetchData();
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao atualizar taxa'));
    } finally {
      setSaving(false);
    }
  };

  const handleRegisterPayment = async () => {
    if (!paymentForm.sellerId || !paymentForm.amount || parseFloat(paymentForm.amount) <= 0) {
      toast.error('Preencha vendedor e valor corretamente');
      return;
    }
    
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append('seller_id', paymentForm.sellerId);
      formData.append('amount', paymentForm.amount);
      if (paymentForm.notes) {
        formData.append('notes', paymentForm.notes);
      }
      
      await axios.post(`${API_URL}/api/commissions/payments`, formData);
      toast.success('Pagamento registrado com sucesso!');
      setPaymentDialog(false);
      setPaymentForm({ sellerId: '', amount: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(getApiError(error, 'Erro ao registrar pagamento'));
    } finally {
      setSaving(false);
    }
  };

  const openEditRateDialog = (seller) => {
    setEditRate({ sellerId: seller.id, rate: seller.commission || 0 });
    setEditRateDialog(true);
  };

  const openPaymentDialog = (seller = null) => {
    setPaymentForm({ 
      sellerId: seller?.id || '', 
      amount: '', 
      notes: '' 
    });
    setPaymentDialog(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="commissions-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <DollarSign className="text-emerald-600" />
            Comissões de Vendedores
          </h1>
          <p className="text-slate-500 mt-1">Controle e relatórios de comissões</p>
        </div>
        {isAdmin && (
          <Button 
            className="bg-emerald-600 hover:bg-emerald-700"
            onClick={() => openPaymentDialog()}
            data-testid="register-payment-btn"
          >
            <CreditCard size={18} className="mr-2" />
            Registrar Pagamento
          </Button>
        )}
      </div>

      {/* Filtros de Período */}
      <Card className="border border-slate-200">
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1">
              <Label className="text-sm">Data Inicial</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-40"
                data-testid="filter-start-date"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-sm">Data Final</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-40"
                data-testid="filter-end-date"
              />
            </div>
            <Button variant="outline" onClick={fetchData}>
              <Calendar size={16} className="mr-2" />
              Filtrar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Cards de Resumo */}
      {commissionData && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total em Vendas</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {formatCurrency(commissionData.summary?.total_sales)}
                  </p>
                </div>
                <TrendingUp className="text-blue-500" size={32} />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total em Comissões</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    {formatCurrency(commissionData.summary?.total_commission)}
                  </p>
                </div>
                <DollarSign className="text-emerald-500" size={32} />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-purple-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Vendedores Ativos</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {commissionData.summary?.sellers_count || 0}
                  </p>
                </div>
                <Users className="text-purple-500" size={32} />
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="pt-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pagamentos Realizados</p>
                  <p className="text-2xl font-bold text-amber-600">
                    {formatCurrency(payments.reduce((sum, p) => sum + (p.amount || 0), 0))}
                  </p>
                </div>
                <PiggyBank className="text-amber-500" size={32} />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs defaultValue="sellers" className="space-y-4">
        <TabsList className="bg-slate-100">
          <TabsTrigger value="sellers" className="data-[state=active]:bg-white">
            <Users size={16} className="mr-2" />
            Vendedores
          </TabsTrigger>
          <TabsTrigger value="commissions" className="data-[state=active]:bg-white">
            <Calculator size={16} className="mr-2" />
            Comissões
          </TabsTrigger>
          <TabsTrigger value="payments" className="data-[state=active]:bg-white">
            <CreditCard size={16} className="mr-2" />
            Pagamentos
          </TabsTrigger>
          <TabsTrigger value="report" className="data-[state=active]:bg-white" onClick={fetchReport}>
            <BarChart3 size={16} className="mr-2" />
            Relatório
          </TabsTrigger>
        </TabsList>

        {/* Tab: Vendedores */}
        <TabsContent value="sellers">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Vendedores e Taxas de Comissão</CardTitle>
              <CardDescription>Gerencie as taxas de comissão de cada vendedor</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="animate-spin text-blue-600" size={32} />
                </div>
              ) : sellers.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Users size={48} className="mx-auto mb-4 text-slate-300" />
                  <p>Nenhum vendedor encontrado</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Vendedor</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Função</TableHead>
                      <TableHead className="text-center">Taxa de Comissão</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sellers.map((seller) => (
                      <TableRow key={seller.id} data-testid={`seller-row-${seller.id}`}>
                        <TableCell className="font-medium">{seller.name}</TableCell>
                        <TableCell>{seller.email}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {seller.role === 'admin' ? 'Administrador' : 'Vendedor'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge className="bg-emerald-100 text-emerald-800 text-lg px-3">
                            {seller.commission || 0}%
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            {isAdmin && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openEditRateDialog(seller)}
                                data-testid={`edit-rate-${seller.id}`}
                              >
                                <Edit2 size={14} className="mr-1" />
                                Editar Taxa
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => fetchSellerSummary(seller.id)}
                              data-testid={`view-details-${seller.id}`}
                            >
                              <Eye size={14} className="mr-1" />
                              Detalhes
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Comissões */}
        <TabsContent value="commissions">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Comissões Calculadas</CardTitle>
              <CardDescription>
                Período: {formatDate(startDate)} a {formatDate(endDate)}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {commissionData?.by_seller?.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Vendedor</TableHead>
                      <TableHead className="text-center">Taxa</TableHead>
                      <TableHead className="text-right">Total Vendas</TableHead>
                      <TableHead className="text-center">Qtd Vendas</TableHead>
                      <TableHead className="text-right">Comissão</TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {commissionData.by_seller.map((item) => (
                      <TableRow key={item.seller_id}>
                        <TableCell className="font-medium">{item.seller_name}</TableCell>
                        <TableCell className="text-center">
                          <Badge variant="outline">{item.commission_rate}%</Badge>
                        </TableCell>
                        <TableCell className="text-right font-medium">
                          {formatCurrency(item.total_sales)}
                        </TableCell>
                        <TableCell className="text-center">{item.total_sales_count}</TableCell>
                        <TableCell className="text-right font-bold text-emerald-600">
                          {formatCurrency(item.total_commission)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => fetchSellerSummary(item.seller_id)}
                            >
                              <Eye size={14} />
                            </Button>
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-emerald-600"
                                onClick={() => openPaymentDialog({ id: item.seller_id, name: item.seller_name })}
                              >
                                <CreditCard size={14} />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <Calculator size={48} className="mx-auto mb-4 text-slate-300" />
                  <p>Nenhuma comissão calculada para o período</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Pagamentos */}
        <TabsContent value="payments">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Histórico de Pagamentos</CardTitle>
              <CardDescription>Pagamentos de comissões realizados</CardDescription>
            </CardHeader>
            <CardContent>
              {payments.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Data</TableHead>
                      <TableHead>Vendedor</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                      <TableHead>Observações</TableHead>
                      <TableHead>Pago por</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {payments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell>{formatDate(payment.created_at)}</TableCell>
                        <TableCell className="font-medium">{payment.seller_name}</TableCell>
                        <TableCell className="text-right font-bold text-emerald-600">
                          {formatCurrency(payment.amount)}
                        </TableCell>
                        <TableCell className="text-slate-500">{payment.notes || '-'}</TableCell>
                        <TableCell>{payment.paid_by_name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <CreditCard size={48} className="mx-auto mb-4 text-slate-300" />
                  <p>Nenhum pagamento registrado no período</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab: Relatório */}
        <TabsContent value="report">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Relatório de Comissões</CardTitle>
                  <CardDescription>Visualize dados agrupados por diferentes critérios</CardDescription>
                </div>
                <Select value={groupBy} onValueChange={setGroupBy}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Agrupar por" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="seller">Por Vendedor</SelectItem>
                    <SelectItem value="day">Por Dia</SelectItem>
                    <SelectItem value="month">Por Mês</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {reportData ? (
                <>
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-blue-600">Total Vendas</p>
                      <p className="text-xl font-bold text-blue-800">
                        {formatCurrency(reportData.totals?.total_sales)}
                      </p>
                    </div>
                    <div className="p-4 bg-emerald-50 rounded-lg">
                      <p className="text-sm text-emerald-600">Total Comissões</p>
                      <p className="text-xl font-bold text-emerald-800">
                        {formatCurrency(reportData.totals?.total_commission)}
                      </p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg">
                      <p className="text-sm text-purple-600">Qtd Vendas</p>
                      <p className="text-xl font-bold text-purple-800">
                        {reportData.totals?.total_count || 0}
                      </p>
                    </div>
                  </div>
                  
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>
                          {groupBy === 'seller' ? 'Vendedor' : groupBy === 'day' ? 'Data' : 'Mês'}
                        </TableHead>
                        {groupBy === 'seller' && <TableHead className="text-center">Taxa</TableHead>}
                        <TableHead className="text-right">Total Vendas</TableHead>
                        <TableHead className="text-center">Qtd</TableHead>
                        <TableHead className="text-right">Comissão</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {reportData.data?.map((item, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">
                            {item.seller_name || item.date || item.month}
                          </TableCell>
                          {groupBy === 'seller' && (
                            <TableCell className="text-center">
                              <Badge variant="outline">{item.commission_rate}%</Badge>
                            </TableCell>
                          )}
                          <TableCell className="text-right">{formatCurrency(item.total_sales)}</TableCell>
                          <TableCell className="text-center">{item.sales_count}</TableCell>
                          <TableCell className="text-right font-bold text-emerald-600">
                            {formatCurrency(item.total_commission)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </>
              ) : (
                <div className="text-center py-8 text-slate-500">
                  <BarChart3 size={48} className="mx-auto mb-4 text-slate-300" />
                  <p>Clique em &quot;Relatório&quot; para carregar os dados</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog: Editar Taxa */}
      <Dialog open={editRateDialog} onOpenChange={setEditRateDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Percent className="text-emerald-600" />
              Editar Taxa de Comissão
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Taxa de Comissão (%)</Label>
              <Input
                type="number"
                step="0.5"
                min="0"
                max="100"
                value={editRate.rate}
                onChange={(e) => setEditRate({ ...editRate, rate: parseFloat(e.target.value) || 0 })}
                className="text-center text-xl"
                data-testid="commission-rate-input"
              />
              <p className="text-xs text-slate-500 text-center">
                Exemplo: Se a taxa for 5%, o vendedor receberá R$ 50,00 em uma venda de R$ 1.000,00
              </p>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setEditRateDialog(false)}>
                Cancelar
              </Button>
              <Button 
                className="flex-1 bg-emerald-600 hover:bg-emerald-700" 
                onClick={handleUpdateRate}
                disabled={saving}
                data-testid="save-rate-btn"
              >
                {saving ? <Loader2 className="animate-spin mr-2" size={18} /> : <Check size={18} className="mr-2" />}
                Salvar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: Registrar Pagamento */}
      <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="text-emerald-600" />
              Registrar Pagamento de Comissão
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Vendedor</Label>
              <Select 
                value={paymentForm.sellerId} 
                onValueChange={(v) => setPaymentForm({ ...paymentForm, sellerId: v })}
              >
                <SelectTrigger data-testid="payment-seller">
                  <SelectValue placeholder="Selecione o vendedor" />
                </SelectTrigger>
                <SelectContent>
                  {sellers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Valor (R$)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={paymentForm.amount}
                onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                placeholder="0,00"
                className="text-right text-xl"
                data-testid="payment-amount"
              />
            </div>
            <div className="space-y-2">
              <Label>Observações (opcional)</Label>
              <Input
                value={paymentForm.notes}
                onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                placeholder="Ex: Pagamento ref. Janeiro/2026"
                data-testid="payment-notes"
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" className="flex-1" onClick={() => setPaymentDialog(false)}>
                Cancelar
              </Button>
              <Button 
                className="flex-1 bg-emerald-600 hover:bg-emerald-700" 
                onClick={handleRegisterPayment}
                disabled={saving}
                data-testid="confirm-payment-btn"
              >
                {saving ? <Loader2 className="animate-spin mr-2" size={18} /> : <Check size={18} className="mr-2" />}
                Registrar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: Detalhes do Vendedor */}
      <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="text-blue-600" />
              Detalhes - {sellerSummary?.seller?.name}
            </DialogTitle>
          </DialogHeader>
          {sellerSummary && (
            <div className="space-y-4">
              {/* Resumo */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <p className="text-xs text-blue-600">Total Vendas</p>
                  <p className="text-lg font-bold text-blue-800">
                    {formatCurrency(sellerSummary.summary?.total_sales)}
                  </p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg text-center">
                  <p className="text-xs text-purple-600">Qtd Vendas</p>
                  <p className="text-lg font-bold text-purple-800">
                    {sellerSummary.summary?.sales_count}
                  </p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-lg text-center">
                  <p className="text-xs text-emerald-600">Comissão Devida</p>
                  <p className="text-lg font-bold text-emerald-800">
                    {formatCurrency(sellerSummary.summary?.total_commission)}
                  </p>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg text-center">
                  <p className="text-xs text-amber-600">Pago</p>
                  <p className="text-lg font-bold text-amber-800">
                    {formatCurrency(sellerSummary.summary?.total_paid)}
                  </p>
                </div>
              </div>
              
              {/* Saldo */}
              <div className={`p-4 rounded-lg text-center ${sellerSummary.summary?.balance > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                <p className="text-sm">{sellerSummary.summary?.balance > 0 ? 'Saldo a Pagar' : 'Saldo'}</p>
                <p className={`text-2xl font-bold ${sellerSummary.summary?.balance > 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {formatCurrency(Math.abs(sellerSummary.summary?.balance || 0))}
                </p>
              </div>
              
              {/* Vendas Recentes */}
              {sellerSummary.recent_sales?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2 text-sm text-slate-600">Últimas Vendas</h4>
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {sellerSummary.recent_sales.map((sale) => (
                      <div key={sale.id} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                        <div>
                          <span className="font-mono text-sm">{sale.sale_number}</span>
                          <span className="text-xs text-slate-500 ml-2">{formatDate(sale.created_at)}</span>
                        </div>
                        <span className="font-medium">{formatCurrency(sale.total)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Commissions;
